import React, { createContext, useContext, useCallback, useMemo, useRef, useState, useEffect } from "react";
import api from "../lib/api";

/**
 * Shared playback state for Tap IN Radio. One `<RadioPlayer />` instance
 * lives at the app root and reads this context. Pages call `playQueue(tracks, idx)`
 * to start playback — the dock takes care of actually loading the media.
 */
const Ctx = createContext(null);

export function RadioPlayerProvider({ children }) {
  const [queue, setQueue] = useState([]); // array of track docs
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(() => {
    const v = parseFloat(localStorage.getItem("tapin_radio_vol") || "0.7");
    return isNaN(v) ? 0.7 : v;
  });
  const [source, setSource] = useState(null); // e.g. "top30" | "playlist:slug" | "station:slug"
  const trackPlayedRef = useRef(new Set()); // dedupe play-count hits within a session

  const current = queue[index] || null;

  const playQueue = useCallback((tracks, i = 0, meta = {}) => {
    const safe = Array.isArray(tracks) ? tracks.filter((t) => t && t.source_url) : [];
    if (safe.length === 0) return;
    setQueue(safe);
    setIndex(Math.max(0, Math.min(i, safe.length - 1)));
    setPlaying(true);
    setSource(meta.source || null);
  }, []);

  const next = useCallback(() => setIndex((i) => Math.min(i + 1, queue.length - 1)), [queue.length]);
  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const toggle = useCallback(() => setPlaying((p) => !p), []);
  const clear = useCallback(() => { setPlaying(false); setQueue([]); setIndex(0); setSource(null); }, []);
  const setVolumePersist = useCallback((v) => {
    setVolume(v);
    try { localStorage.setItem("tapin_radio_vol", String(v)); } catch {}
  }, []);

  // When the current track changes, ping the backend play-count endpoint
  useEffect(() => {
    if (!current?.id) return;
    const key = `${current.id}:${Date.now() >> 17}`; // coarse 15-min bucket
    if (trackPlayedRef.current.has(key)) return;
    trackPlayedRef.current.add(key);
    // Best effort — does not require auth.
    const slug = current.station_slug || "";
    api.post(`/radio/stations/${slug || "_"}/tracks/${current.id}/play`).catch(() => {});
  }, [current?.id, current?.station_slug]);

  const value = useMemo(() => ({
    queue, index, playing, volume, source, current,
    playQueue, next, prev, toggle, clear, setVolume: setVolumePersist,
  }), [queue, index, playing, volume, source, current, playQueue, next, prev, toggle, clear, setVolumePersist]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRadioPlayer() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useRadioPlayer must be used inside RadioPlayerProvider");
  return v;
}
